/*******************************
 * This component uses jotai async derived atoms, 
 * so it requires suspense wrapper around it when used
/*******************************/
import { useAtomValue, useSetAtom } from 'jotai';
import React from 'react';
import { currentChartTypeAtom } from 'views/state';

import { SASTType } from './config';
import { getSASTSourceType } from './helper';
import {
  vulnBySeverityChartDataAtom,
  sastCurrentVulnCountAtom,
  severityChartLabelsAtom,
  vulnBySeverityChartDataCountAtom,
  vulnGroupsChartDataAtom,
  vulnByStatusChartDataAtom,
  statusChartLabelsAtom,
  severityFiltersAtom,
  sastTypeAtom,
  appliedRowIdsAtom,
  dashboardDataAtom,
} from './state';
import BarOrDoughnutChart from '../components/BarOrDoughnutChart';
import GroupingChart from '../components/GroupingChart';
import {
  getDrillDownUrl,
  getVulnerabilityStatusValue,
  getSeverityValue,
  getSeverityFilterValues,
  checkNonZeroArray,
  checkNonZeroObj,
} from '../helper';
import { Severity, VulnerabilityStatus } from '../types';

import '../styles.css';

type Props = {
  setModalComponentData: (ModalDialogData) => void;
};

const CurrentCharts = ({ setModalComponentData }: Props) => {
  const sastType = useAtomValue(sastTypeAtom);
  const currentChartType = useAtomValue(currentChartTypeAtom);
  const severityFilters = useAtomValue(severityFiltersAtom);
  const setCurrentVulnCount = useSetAtom(sastCurrentVulnCountAtom);

  const vulnBySeverityChartData = useAtomValue(vulnBySeverityChartDataAtom);
  const vulnBySeverityChartDataCount = useAtomValue(
    vulnBySeverityChartDataCountAtom,
  );
  const severityChartLabels = useAtomValue(severityChartLabelsAtom);
  const vulnGroupsChartData = useAtomValue(vulnGroupsChartDataAtom);
  const vulnByStatusChartData = useAtomValue(vulnByStatusChartDataAtom);
  const statusChartLabels = useAtomValue(statusChartLabelsAtom);
  const appliedSASTIds = useAtomValue(appliedRowIdsAtom);
  const dashboardData = useAtomValue(dashboardDataAtom);

  function getChartTooltipLabels(sourceData, sastType: SASTType) {
    const toolTipLabels: string[] = [];
    const bug_values = Object.values<number>(sourceData[SASTType.Bug]);
    const vuln_values = Object.values<number>(
      sourceData[SASTType.Vulnerability],
    );
    if (
      (sastType === SASTType.Bug || sastType === SASTType.All) &&
      checkNonZeroArray(bug_values)
    ) {
      toolTipLabels.push(SASTType.Bug);
    }

    if (
      (sastType === SASTType.Vulnerability || sastType === SASTType.All) &&
      checkNonZeroArray(vuln_values)
    ) {
      toolTipLabels.push(SASTType.Vulnerability);
    }

    return toolTipLabels;
  }

  function getVulnByStatusChartTooltipLabels(data, sastType: SASTType) {
    const toolTipLabels: string[] = [];
    const bug_values = Object.values<number>(data[SASTType.Bug]);
    const vuln_values = Object.values<number>(data[SASTType.Vulnerability]);
    if (
      (sastType === SASTType.Bug || sastType === SASTType.All) &&
      checkNonZeroObj(bug_values)
    ) {
      toolTipLabels.push(SASTType.Bug);
    }

    if (
      (sastType === SASTType.Vulnerability || sastType === SASTType.All) &&
      checkNonZeroObj(vuln_values)
    ) {
      toolTipLabels.push(SASTType.Vulnerability);
    }

    return toolTipLabels;
  }

  function getSeverityChartsDrillDownUrl(
    label: string,
    sourceType: string,
    patchable = false,
    obsolete = false,
  ) {
    if (sourceType == 'Bug') sourceType = 'BUG';
    if (sourceType == 'Vulnerability') sourceType = 'VULNERABILITY';
    return getDrillDownUrl({
      type: 'open',
      severity: getSeverityValue(Severity[label]).toString(),
      sourceType: 'sast2',
      issueType: sourceType,
      qid: '',
      patchable: patchable,
      obsolete: obsolete,
      severity_in: undefined,
      hosts_in: appliedSASTIds,
    });
  }

  function getStatusChartsDrillDownUrl(status: string, sourceType: string) {
    // handle statuses, as the labels are customized
    if (status.toLocaleLowerCase().startsWith('new')) {
      status = VulnerabilityStatus.New;
    } else if (status.toLocaleLowerCase().startsWith('reopened')) {
      status = VulnerabilityStatus.Reopened;
    } else if (status.toLocaleLowerCase().startsWith('active')) {
      status = VulnerabilityStatus.Active;
    } else if (status.toLocaleLowerCase().startsWith('fixed')) {
      status = VulnerabilityStatus.Fixed;
    } else if (status.toLocaleLowerCase().startsWith('ignored')) {
      status = VulnerabilityStatus.Ignored;
    }
    if (sourceType == 'Bug') sourceType = 'BUG';
    if (sourceType == 'Vulnerability') sourceType = 'VULNERABILITY';

    return getDrillDownUrl({
      type: getVulnerabilityStatusValue(status as VulnerabilityStatus),
      severity: '',
      sourceType: getSASTSourceType(sourceType as SASTType),
      issueType: sourceType,
      qid: '',
      severity_in: getSeverityFilterValues(severityFilters),
      hosts_in: appliedSASTIds,
    });
  }

  function getGroupingDrillDownUrl(ruleId: string, sourceType: string) {
    return getDrillDownUrl({
      type: 'open',
      severity: '',
      sourceType: 'sast2',
      issueType: 'All',
      qid: '',
      rule_id: ruleId,
      // search: search,
      severity_in: getSeverityFilterValues(severityFilters),
      hosts_in: appliedSASTIds,
    });
  }

  return (
    <>
      {vulnBySeverityChartData && (
        <>
          <div className="col-md-6">
            <BarOrDoughnutChart
              chartType={currentChartType}
              title="By Severity"
              labels={severityChartLabels}
              tooltipLabels={getChartTooltipLabels(
                dashboardData.vulns_by_severity,
                sastType,
              )}
              datasets={vulnBySeverityChartData}
              severityFilters={severityFilters}
              total_count={vulnBySeverityChartDataCount}
              getDrillDownUrl={getSeverityChartsDrillDownUrl}
              setCurrentVulnCount={setCurrentVulnCount}
            />
          </div>
        </>
      )}

      {vulnByStatusChartData && (
        <div className="col-md-6">
          <BarOrDoughnutChart
            chartType={currentChartType}
            title="By Status"
            labels={statusChartLabels}
            tooltipLabels={getVulnByStatusChartTooltipLabels(
              dashboardData.vulns_by_status,
              sastType,
            )}
            datasets={vulnByStatusChartData}
            severityFilters={Object.values(VulnerabilityStatus)}
            getDrillDownUrl={getStatusChartsDrillDownUrl}
          />
        </div>
      )}

      {vulnGroupsChartData && (
        <>
          {
            <div className="col-md-12">
              <GroupingChart
                chartType={currentChartType}
                title="Group by Issues"
                data={vulnGroupsChartData}
                onExpandClick={setModalComponentData}
                severityFilters={severityFilters}
                sourceType={getSASTSourceType(sastType)}
                getDrillDownUrl={getGroupingDrillDownUrl}
                showQidColumn={false}
              />
            </div>
          }
        </>
      )}
    </>
  );
};

export default CurrentCharts;
